import axios from "@/plugins/axios";
import utils from "@/stores/utils";

/**
 * Recipients of a campaign: one row per contact, with its delivery status.
 */

const DEFAULT_FILTERS = { offset: 0, limit: 50, ordering: "customer_name", is_test: false };

const createState = (options = {}) => ({
    recipients: [],
    recipients_url: null,
    recipients_count: 0,
    recipients_filters: { ...DEFAULT_FILTERS },
    recipients_loaded: false,
    recipients_loading: false,
    recipients_loading_errors: null,
    selected_items: [],
    adding_recipients: false,
    sending: false,
    cancelTokenSource: null,
    _storageOptions: options || {},
});

const mutations = {
    updateRecipients(state, recipients) {
        state.recipients = recipients;
        state.recipients_loaded = true;
        state.recipients_loading_errors = null;
    },
    updateRecipientsCount(state, count) {
        state.recipients_count = count;
    },
    updateRecipientsUrl(state, url) {
        state.recipients_url = url;
    },
    updateRecipientsLoading(state, loading) {
        state.recipients_loading = loading;
    },
    updateRecipientsLoadingErrors(state, errors) {
        state.recipients_loading_errors = errors;
        state.recipients_loading = false;
    },
    updateRecipientsFilters(state, filters) {
        state.recipients_filters = filters;
    },
    updateSelectedItems(state, items) {
        state.selected_items = items;
    },
    updateAddingRecipients(state, adding) {
        state.adding_recipients = adding;
    },
    updateSending(state, sending) {
        state.sending = sending;
    },
    markRecipientsQueued(state, recipientIds) {
        const queued = new Set((recipientIds || []).map((id) => Number(id)));
        state.recipients = state.recipients.map((recipient) =>
            queued.has(Number(recipient.id)) &&
            (recipient.status === "pending" || recipient.status === "failed")
                ? { ...recipient, status: "queued" }
                : recipient
        );
    },
    updateCancelTokenSource(state, source) {
        state.cancelTokenSource = source;
    },
};

const actions = {
    async fetchRecipients({ commit, state }) {
        if (!state.recipients_url) {
            return;
        }
        if (state.cancelTokenSource) {
            state.cancelTokenSource.cancel("New fetch started");
        }
        const source = axios.CancelToken.source();
        commit("updateCancelTokenSource", source);
        commit("updateRecipientsLoading", true);

        try {
            const response = await axios.get(state.recipients_url, {
                params: state.recipients_filters,
                cancelToken: source.token,
            });
            commit("updateRecipients", response.data.results);
            commit("updateRecipientsCount", response.data.count);
            return response.data;
        } catch (xhr_error) {
            if (axios.isCancel(xhr_error)) {
                return;
            }
            const error = utils.handleError(xhr_error);
            commit("updateRecipientsLoadingErrors", error.details);
            throw error;
        } finally {
            commit("updateRecipientsLoading", false);
        }
    },

    /** How many emails an ``addRecipients`` would create, and why some are left out. */
    async previewRecipients({ state }, params) {
        try {
            const response = await axios.post(`${state.recipients_url}preview/`, params);
            return response.data;
        } catch (xhr_error) {
            throw utils.handleError(xhr_error);
        }
    },

    async addRecipients({ commit, dispatch, state }, params) {
        commit("updateAddingRecipients", true);
        try {
            const response = await axios.post(`${state.recipients_url}add/`, params);
            await dispatch("fetchRecipients");
            return response.data;
        } catch (xhr_error) {
            throw utils.handleError(xhr_error);
        } finally {
            commit("updateAddingRecipients", false);
        }
    },

    async removeRecipients({ commit, dispatch, state }, params) {
        try {
            const response = await axios.post(`${state.recipients_url}remove/`, {
                recipients: params.recipients,
            });
            commit("updateSelectedItems", []);
            await dispatch("fetchRecipients");
            return response.data;
        } catch (xhr_error) {
            throw utils.handleError(xhr_error);
        }
    },

    async sendRecipient({ commit, dispatch, state }, params) {
        commit("updateSending", true);
        try {
            const response = await axios.post(
                `${state.recipients_url}${params.recipient.id}/send/`
            );
            const queuedIds = response.data.recipients || [params.recipient.id];
            commit("markRecipientsQueued", queuedIds);
            commit("campaign/decrementPendingCount", queuedIds.length, { root: true });
            await dispatch("fetchRecipients");
            return response.data;
        } catch (xhr_error) {
            throw utils.handleError(xhr_error);
        } finally {
            commit("updateSending", false);
        }
    },

    async sendTest({ commit, state }, params) {
        commit("updateSending", true);
        try {
            const response = await axios.post(`${state.recipients_url}test/`, params);
            return response.data;
        } catch (xhr_error) {
            throw utils.handleError(xhr_error);
        } finally {
            commit("updateSending", false);
        }
    },

    async retryFailed({ commit, dispatch, state }) {
        commit("updateSending", true);
        try {
            const response = await axios.post(`${state.recipients_url}retry-failed/`);
            commit("markRecipientsQueued", response.data.recipients || []);
            await dispatch("fetchRecipients");
            return response.data;
        } catch (xhr_error) {
            throw utils.handleError(xhr_error);
        } finally {
            commit("updateSending", false);
        }
    },

    async fetchRecipient({ state }, params) {
        try {
            const recipientId = params.recipient;
            const campaignId = params.campaign;
            let url = null;
            if (campaignId) {
                url = `/api/campaigns/${campaignId}/recipients/${recipientId}/`;
            } else if (state.recipients_url) {
                url = `${state.recipients_url}${recipientId}/`;
            }
            if (!url) {
                throw utils.handleError({
                    message: "Missing campaign for recipient detail",
                });
            }
            const response = await axios.get(url);
            return response.data;
        } catch (xhr_error) {
            throw utils.handleError(xhr_error);
        }
    },

    async init({ commit }, params) {
        commit("updateRecipientsUrl", params?.url || null);
        commit("updateRecipientsFilters", {
            ...DEFAULT_FILTERS,
            ...(params?.filters || {}),
        });
        commit("updateRecipients", []);
        commit("updateSelectedItems", []);
    },
};

export default (options = {}) => ({
    namespaced: true,
    state: createState(options),
    mutations,
    actions,
});
